// client/src/pages/sections/groups/ResolveModal.js
// The Resolve-panel modal surfaced from every Client Groups DQ issue row's
// action button. Renders an issue-level verb list (driven by the registry
// in resolveVerbConfig.js), an inline confirmation step per verb with
// support for per-verb form fields (radio for pick_truth.side, text for
// dismiss.reason, etc.), and POSTs to /api/diResolveAction on confirm.
//
// Client-level verbs (mark_nlc) are NOT here — they're per-MemberCard
// buttons (see MarkNlcButton.js).

import { useEffect, useState } from 'react';
import { verbsForIssue } from './resolveVerbConfig';
import { ISSUE_META, issueSig } from './DataQualityPanel';
import { useOptimistic } from './optimisticContext';
import { emitToast, errorText } from './toast';
import { postResolveAction } from './resolveActionQueue';
import { loadAnchors, getAnchors } from './clientRosterCache';
import GroupAnchorPicker from './GroupAnchorPicker';
import ClientChip from './ClientChip';
import { useSubmitGuard } from './useSubmitGuard';

// Matches a #####-### client code anywhere in a string (the
// GroupAnchorPicker stores "code — name", so anchorPicker fields need
// their value normalized to the bare code before submit).
const CODE_RE = /(\d{5}-\d{3})/;

export default function ResolveModal({ issue, onClose, onResolved, onExpandRow, testMode, testSessionId, initialVerb }) {
  // verbsForThisIssue is the full applicable set — used for dispatch
  // (initialVerb lookup) and singleVerb detection. visibleVerbs is
  // what the user actually sees in the verb-list step; verbs flagged
  // hideFromVerbList (today: ignore_sg_suggestion, which is surfaced
  // as a per-row Action column button instead) are filtered out.
  const verbsForThisIssue = issue ? verbsForIssue(issue.issueCode) : [];
  const visibleVerbs      = verbsForThisIssue.filter(v => !v.hideFromVerbList);
  const singleVerb        = visibleVerbs.length === 1;
  // initialVerb prop lets the caller (IssueRow's "Ignore" button)
  // pre-select a specific verb to land directly on its confirmation
  // step, skipping the verb-list. Looks up against the full set so
  // verbs hidden from the list are still dispatchable.
  const preselected = initialVerb
    ? verbsForThisIssue.find(v => v.key === initialVerb) || null
    : null;
  // If the (auto-)single verb redirects to the row's drilldown
  // (split_from_group), don't pre-select it — the openRowDrilldown
  // useEffect handles the short-circuit-and-close path.
  const initialActiveVerb = preselected
    ? preselected
    : (singleVerb && !visibleVerbs[0].openRowDrilldown)
      ? visibleVerbs[0]
      : null;
  const [activeVerb, setActiveVerb] = useState(() => initialActiveVerb);
  const [submitting] = useState(false);   // optimistic flow closes modal; no live submitting toggle
  const guard = useSubmitGuard();         // one submit at a time: a double-click or a re-click mid-wait is ignored
  const [error, setError]           = useState(null);
  const [formValues, setFormValues] = useState({});
  const { dismissByIds, dismissByClientCode, restoreIds, markPending, clearPending } = useOptimistic();

  // Close on Esc.
  useEffect(() => {
    if (!issue) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [issue, submitting, onClose]);

  // Single-verb mode + verb is a row-drilldown redirect → don't render
  // the modal at all. Expand the row and close synchronously on mount.
  // Runs once per issue id.
  useEffect(() => {
    if (!issue) return;
    if (singleVerb && visibleVerbs[0].openRowDrilldown) {
      onExpandRow && onExpandRow();
      onClose();
    }
    // We deliberately depend only on issue?.id — visibleVerbs is
    // recomputed each render and would re-fire this effect every time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?.id]);

  if (!issue) return null;

  // Drilldown-redirect issues (e.g. SG_TRUST_ESTATE_MIXED, whose only verb is
  // split_from_group) don't use the modal at all — the effect above expands
  // the row and closes. Render NOTHING so the overlay never paints; otherwise
  // it flashes open for one frame before the effect closes it.
  if (singleVerb && visibleVerbs[0] && visibleVerbs[0].openRowDrilldown) return null;

  // The verb-list step renders ONLY the visible verbs. Verbs flagged
  // hideFromVerbList stay dispatchable via initialVerb but never
  // appear as a clickable button here.
  const verbs = visibleVerbs;

  function startVerb(verb) {
    // Row-drilldown verbs short-circuit: don't render the confirmation
    // step, just expand the row and close the modal.
    if (verb.openRowDrilldown) {
      onExpandRow && onExpandRow();
      onClose();
      return;
    }
    setActiveVerb(verb);
    setError(null);
    // Initialize form values to {} so required-field validation behaves.
    setFormValues({});
  }

  function backToList() {
    setActiveVerb(null);
    setError(null);
    setFormValues({});
  }

  async function submit() {
    if (!activeVerb) return;
    // Validate required fields. Honor `showWhen` so hidden fields don't
    // gate submission, and resolve `formFields` if declared as a function.
    // A verb can say it isn't applicable yet (blockedReason) — the confirm button is disabled
    // then, this is the backstop.
    if (typeof activeVerb.blockedReason === 'function' && activeVerb.blockedReason(issue)) return;
    const fields = resolveFormFields(activeVerb, issue);
    const visible = fields.filter(f => !f.showWhen || f.showWhen(formValues, issue));
    const missing = visible.filter(f => f.required && !formValues[f.name]);
    if (missing.length) {
      setError(`Please complete: ${missing.map(f => f.label).join(', ')}.`);
      return;
    }
    // Normalize anchorPicker values ("code — name") to the bare client
    // code the verb handlers expect. Mutates a copy so the displayed
    // form value stays human-readable.
    const submitValues = { ...formValues };
    // A confirmation toggled for one pick and then hidden by another (e.g. switching the Pick-truth
    // side) must not ride along: the server refuses a confirmation the group doesn't need.
    for (const f of fields) {
      if (/^mixedConfirmation/.test(f.name) && !visible.includes(f)) delete submitValues[f.name];
    }
    for (const f of visible) {
      if (f.type === 'anchorPicker' && submitValues[f.name]) {
        const m = String(submitValues[f.name]).match(CODE_RE);
        if (!m) {
          setError(`Pick a valid Relationship Group for "${f.label}".`);
          return;
        }
        submitValues[f.name] = m[1];
      }
    }
    // UI-only verbs (today: split_from_group) skip the API call. They
    // exist so partners always see a "actually split this" alternative on
    // the verb-list, but the actual mutation happens via per-card Move…
    // buttons in the expanded row.
    if (activeVerb.noApiSubmit) {
      onClose();
      return;
    }

    // Optimistic: hide the issue immediately and close the modal. Fire
    // the request in the background. On failure, restore the row and
    // surface a toast so the partner knows it didn't stick.
    const issueId   = issue.id;
    const verbLabel = activeVerb.label || activeVerb.key;
    // issueKey is the server's fallback when issueId is stale (which
    // happens after revalidate's DELETE+INSERT regenerates IDs). The
    // server resolves to the currently-open issue with the same
    // (issueCode, primaryClientCode, affectedCodes) signature.
    const issueKey = {
      issueCode:         issue.issueCode,
      primaryClientCode: issue.primaryClientCode,
      affectedCodes:     issue.affectedCodes,
    };
    // Step-5 "group two standalones": rescuing a standalone into ANOTHER
    // standalone's Relationship Group resolves BOTH at once — the rescued
    // client (this issue) AND the target, which is no longer a one-client
    // standalone RG. We hide the target's row too so both vanish immediately;
    // the backend drops the target's STANDALONE issue on the next rebuild.
    // (No-op when the target is a real multi-client RG anchor with no open
    // issue of its own — dismissByClientCode returns nothing to revert.)
    const groupTarget = activeVerb.key === 'rescue_to_rg' ? submitValues.targetRg : null;
    // Pending-by-code: hide every issue referencing any code this action
    // touches (the whole cluster) for the full lifecycle, so rapid
    // accept/ignore on overlapping suggestions doesn't flicker rows back.
    const pendingCodesForAction = [
      issue.primaryClientCode,
      ...(issue.affectedCodes || []),
      groupTarget,
    ].filter(Boolean);
    // Dismiss by STABLE SIGNATURE (not issue.id): ids regenerate on every
    // rebuild, so an id-based dismiss is pruned the moment any queued action's
    // refetch lands — making not-yet-processed items flash back into view. A
    // signature survives the rebuild, so this item stays hidden until its own
    // action resolves it. This is the durable hide; markPending only adds the
    // loading state on OTHER rows that share a code (overlapping clusters).
    const sig = issueSig(issue);
    markPending(pendingCodesForAction);
    dismissByIds([sig]);
    // Also hide the rescue target's own standalone row (if it has one).
    const targetSigs = groupTarget ? dismissByClientCode(groupTarget) : [];
    onClose();
    // Optimistic confirm: the row is removed and the per-step counts /
    // gating update straight from that optimistic dismiss, so the partner
    // gets an instant "saved" toast and never waits on a recalculation.
    //
    // IMPORTANT — keep the code-based hide (markPending) until THIS action's
    // refetch has reconciled, hence `await onResolved` before clearPending.
    // rebuildDqIssues regenerates issue ids on every revalidate, so the
    // id-based dismiss (dismissByIds) gets pruned the moment any concurrent
    // action's refetch lands. Clearing the (id-independent) pending-code
    // hide too early would let the row flash back in that window. The await
    // is invisible — the modal is already closed and the toast already shown.
    emitToast({ kind: 'success', message: 'Change saved.' });
    try {
      const data = await postResolveAction({
        verb:      activeVerb.key,
        issueId,
        issueKey,
        payload:   submitValues,
        testMode:  Boolean(testMode),
        sessionId: testMode ? testSessionId : null,
      });
      if (onResolved) await onResolved(data);
    } catch (e) {
      restoreIds([sig, ...targetSigs]);
      emitToast({
        kind: 'error',
        message: `Couldn't ${verbLabel.toLowerCase()}: ${errorText(e)}. Restored.`,
      });
    } finally {
      clearPending(pendingCodesForAction);
    }
  }

  // When the active verb hosts an anchor picker (rescue_to_rg / assign_rg),
  // the form can carry a long quick-pick list of the partner's Relationship
  // Groups. In that mode we pin the modal height and let ONLY that list scroll,
  // so the help text and Back/Apply buttons stay visible without scrolling.
  const hasAnchorList = Boolean(
    activeVerb && resolveFormFields(activeVerb, issue).some(f => f.type === 'anchorPicker')
  );

  return (
    <div
      className="cg-resolve-overlay"
      onClick={() => !submitting && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cg-resolve-title"
    >
      <div
        className={`cg-resolve-modal ${hasAnchorList ? 'cg-resolve-modal--fixed' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cg-resolve-header">
          <h3 id="cg-resolve-title">Resolve issue</h3>
          <button
            type="button"
            className="cg-resolve-close"
            disabled={submitting}
            onClick={onClose}
            aria-label="Close"
          >×</button>
        </div>

        <div className="cg-resolve-summary">
          <div className="cg-resolve-summary-row">
            <ClientChip code={issue.primaryClientCode} name={issue.primaryClientName} />
            {!issue.primaryClientName && <span className="cg-cell-hint">(not in PE)</span>}
          </div>
          <div
            className="cg-resolve-summary-code"
            title={issue.issueCode /* developer reference on hover */}
          >
            <strong>{ISSUE_META[issue.issueCode]?.label || issue.issueCode}</strong>
          </div>
        </div>

        {!activeVerb ? (
          <div className="cg-resolve-verbs">
            {verbs.length === 0 ? (
              issue.issueCode === 'SG_PARENT_INACTIVE_IN_PE' ? (
                // No issue-level verb: the fix is the per-member button on the row's member list.
                <p className="cg-resolve-empty">
                  <strong>This is fixed from the member list.</strong> Close this
                  window, open the row (click it to expand the Signer Group&rsquo;s members),
                  and press <strong>Make SG Parent</strong> on the member who should lead
                  the group. The other members then follow that parent, and this issue clears.
                </p>
              ) : (
                <p className="cg-resolve-empty">
                  No issue-level resolution actions are wired up for this issue
                  type yet. (Mark NLC is available as a per-client action on
                  each member card below.)
                </p>
              )
            ) : (
              verbs.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className={`cg-resolve-verb cg-resolve-verb--${v.kind}`}
                  onClick={() => startVerb(v)}
                >
                  {v.label}
                </button>
              ))
            )}
          </div>
        ) : (
          <ConfirmationStep
            verb={activeVerb}
            issue={issue}
            values={formValues}
            onChange={setFormValues}
            submitting={submitting}
            error={error}
            // Single-verb mode skips the verb list, so "Back" should close
            // the modal entirely rather than land on an empty selector.
            // When the partner landed straight on this verb (singleVerb
            // mode OR preselected via initialVerb), there's no list to
            // return to — Back closes the modal instead.
            onBack={(singleVerb || preselected) ? onClose : backToList}
            onConfirm={() => guard(submit)}
          />
        )}
      </div>
    </div>
  );
}

function resolveFormFields(verb, issue) {
  const raw = typeof verb.formFields === 'function'
    ? verb.formFields(issue)
    : (verb.formFields || []);
  return Array.isArray(raw) ? raw : [];
}

function ConfirmationStep({ verb, issue, values, onChange, submitting, error, onBack, onConfirm }) {
  const { confirmation } = verb;
  const fields = resolveFormFields(verb, issue);
  const body = typeof confirmation.body === 'function'
    ? confirmation.body(issue)
    : confirmation.body;
  const isDanger = confirmation.tone === 'danger';
  // Optional verb-level precondition (e.g. rehome_nlc_group's RG task waits for the signer group
  // task): shown instead of the form, and Confirm is disabled.
  const blockedReason = typeof verb.blockedReason === 'function' ? verb.blockedReason(issue) : null;

  return (
    <div className="cg-resolve-confirm">
      <h4 className="cg-resolve-confirm-title">{confirmation.title}</h4>
      <p className="cg-resolve-confirm-body">{body}</p>

      {blockedReason && (
        <p className="cg-resolve-empty" role="status">
          <i className="fas fa-lock" aria-hidden="true" /> {blockedReason}
        </p>
      )}

      {!blockedReason && fields.length > 0 && (
        <div className="cg-resolve-form">
          {fields
            .filter(f => !f.showWhen || f.showWhen(values, issue))
            .map(f => (
              <FormField
                key={f.name}
                field={f}
                issue={issue}
                value={values[f.name]}
                onChange={(v) => onChange({ ...values, [f.name]: v })}
                disabled={submitting}
              />
            ))}
        </div>
      )}

      {error && (
        <div className="cg-resolve-error" role="alert">
          <strong>Couldn't resolve:</strong> {error}
        </div>
      )}

      <div className="cg-resolve-actions">
        <button
          type="button"
          className="cg-btn cg-btn--secondary"
          disabled={submitting}
          onClick={onBack}
        >
          {confirmation.cancelLabel || 'Cancel'}
        </button>
        <button
          type="button"
          className={`cg-btn ${isDanger ? 'cg-btn--danger' : 'cg-btn--primary'}`}
          disabled={submitting || Boolean(blockedReason)}
          onClick={onConfirm}
        >
          {submitting ? 'Working…' : (confirmation.confirmLabel || 'Confirm')}
        </button>
      </div>
    </div>
  );
}

function FormField({ field, issue, value, onChange, disabled }) {
  if (field.type === 'radio') {
    const options = typeof field.options === 'function'
      ? field.options(issue)
      : (field.options || []);
    return (
      <fieldset className="cg-resolve-field cg-resolve-field--radio">
        <legend className="cg-resolve-field-label">
          {field.label}
          {field.required && <span className="cg-resolve-required" aria-hidden="true"> *</span>}
        </legend>
        {options.map(opt => (
          <label key={opt.value} className="cg-resolve-radio-row">
            <input
              type="radio"
              name={field.name}
              value={opt.value}
              checked={value === opt.value}
              disabled={disabled}
              onChange={() => onChange(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </fieldset>
    );
  }
  if (field.type === 'checkbox') {
    // Required checkbox = partner must explicitly toggle the slider to
    // satisfy the field's `required` validator. Used by the mixed-
    // type-confirmation gate on accept_sg_suggestion and pick_truth.
    // Rendered as a slider sitting to the right of the explanation so
    // the long-form copy reads first and the partner's affirmation
    // action is on the side. value is `field.checkedValue` when on,
    // null when off.
    const checkedValue = field.checkedValue || 'yes';
    const isChecked    = value === checkedValue;
    return (
      <div className="cg-resolve-field cg-resolve-field--toggle">
        <div className="cg-resolve-toggle-row">
          <div className="cg-resolve-toggle-text">
            {field.label}
            {field.required && <span className="cg-resolve-required" aria-hidden="true"> *</span>}
          </div>
          <label
            className={`cg-resolve-toggle ${disabled ? 'disabled' : ''}`}
            aria-label="Confirm"
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={disabled}
              onChange={(e) => onChange(e.target.checked ? checkedValue : null)}
            />
            <span className="cg-resolve-toggle-slider" />
          </label>
        </div>
      </div>
    );
  }
  if (field.type === 'text') {
    return (
      <label className="cg-resolve-field cg-resolve-field--text">
        <span className="cg-resolve-field-label">
          {field.label}
          {field.required && <span className="cg-resolve-required" aria-hidden="true"> *</span>}
        </span>
        <input
          type="text"
          value={value || ''}
          disabled={disabled}
          placeholder={field.placeholder || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
    );
  }
  if (field.type === 'select') {
    return (
      <SelectField
        field={field}
        issue={issue}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  if (field.type === 'anchorPicker') {
    return (
      <AnchorPickerField
        field={field}
        issue={issue}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  return null;
}

// Searchable picker over ALL existing SG or RG anchors (field.anchorType
// = 'sg' | 'rg'), backed by the shared dgGroupAnchors cache. Used by
// assign_rg / rescue_to_rg so the partner can pick any existing
// Relationship Group — parity with the SplitMemberButton SG picker —
// instead of only the handful of suggestion-derived candidates.
// Pick the right anchor list for a field from the dgGroupAnchors cache.
// For an RG picker with field.includeOrphans, ungrouped orphan clients are
// appended (flagged isOrphan) so the partner can start a NEW Relationship
// Group anchored on one — used by assign_rg / rescue_to_rg, whose verbs
// accept any active client_groups row as the target.
function anchorsForField(cache, field) {
  if (!cache) return [];
  if (field.anchorType !== 'rg') return cache.sgAnchors || [];
  const rg = cache.rgAnchors || [];
  return field.includeOrphans ? [...rg, ...(cache.rgOrphans || [])] : rg;
}

function AnchorPickerField({ field, issue, value, onChange, disabled }) {
  const [anchors, setAnchors] = useState(() => anchorsForField(getAnchors(), field));
  useEffect(() => {
    let cancelled = false;
    // fresh: the list is a copy taken when the page loaded, and the partner's own earlier steps (re-anchoring a group,
    // making a new one) change which clients anchor a group; a stale copy hid the group she was looking for.
    loadAnchors({ fresh: true })
      .then(a => {
        if (cancelled) return;
        setAnchors(anchorsForField(a, field));
      })
      .catch(() => { /* keep whatever the cache gave us */ });
    return () => { cancelled = true; };
    // field is a stable module-level config object (resolveVerbConfig).
  }, [field]);

  // Optional quick-pick list (e.g. "your Relationship Groups") shown above the
  // full search, so the common case is one click and the firm-wide search is
  // the fallback. field.loadPreferred() returns [{ clientCode, clientName }].
  const [preferred, setPreferred] = useState([]);
  useEffect(() => {
    if (typeof field.loadPreferred !== 'function') return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => field.loadPreferred())
      .then(list => { if (!cancelled) setPreferred(Array.isArray(list) ? list : []); })
      .catch(() => { /* fall back to search-only */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCode = (String(value || '').match(CODE_RE) || [])[1] || null;

  // Exclude self-references (e.g. a standalone's own current RG — you can't
  // rescue a Relationship Group into itself). field.excludeAnchor(issue)
  // returns a code or array of codes to drop from both the quick-picks and
  // the full search.
  const ex = typeof field.excludeAnchor === 'function' ? field.excludeAnchor(issue) : null;
  const excludeSet = new Set((Array.isArray(ex) ? ex : [ex]).filter(Boolean));
  const visibleAnchors   = excludeSet.size ? anchors.filter(a => !excludeSet.has(a.clientCode))   : anchors;
  const visiblePreferred = excludeSet.size ? preferred.filter(a => !excludeSet.has(a.clientCode)) : preferred;

  // "Your Relationship Groups" (dgMyRgAnchors, fetched live) are a subset of all
  // RGs, but a JUST-created self-anchored RG can be present there while missing
  // from the firm-wide anchor cache (dgGroupAnchors is primed once at mount and
  // not busted after a resolve). Union the quick-picks into the searchable set
  // so anything shown as a quick-pick can also be found by typing in the search.
  const searchAnchors = (() => {
    const byCode = new Map();
    for (const a of visibleAnchors)   byCode.set(a.clientCode, a);
    for (const a of visiblePreferred) if (!byCode.has(a.clientCode)) byCode.set(a.clientCode, a);
    return [...byCode.values()];
  })();

  return (
    <label className="cg-resolve-field cg-resolve-field--text">
      <span className="cg-resolve-field-label">
        {field.label}
        {field.required && <span className="cg-resolve-required" aria-hidden="true"> *</span>}
      </span>

      {visiblePreferred.length > 0 && (
        <div className="cg-rg-quickpick">
          <span className="cg-rg-quickpick-label">{field.preferredLabel || 'Your Relationship Groups'}</span>
          <div className="cg-rg-quickpick-list">
            {visiblePreferred.map(a => {
              const label = a.clientName ? `${a.clientCode} — ${a.clientName}` : a.clientCode;
              const isSel = selectedCode && a.clientCode === selectedCode;
              return (
                <button
                  type="button"
                  key={a.clientCode}
                  className={`cg-rg-quickpick-btn ${isSel ? 'cg-rg-quickpick-btn--sel' : ''}`}
                  disabled={disabled}
                  onClick={() => onChange(label)}
                  title={label}
                >
                  <ClientChip code={a.clientCode} name={a.clientName} />
                </button>
              );
            })}
          </div>
          <span className="cg-rg-quickpick-or">Not one of yours? Search all Relationship Groups below.</span>
        </div>
      )}

      <GroupAnchorPicker
        anchors={searchAnchors}
        value={value || ''}
        disabled={disabled}
        onChange={onChange}
        placeholder={field.placeholder || 'Start typing code or name…'}
      />
    </label>
  );
}

// Select with either static `options` (array or (issue)=>array) or async
// `loadOptions(issue)` returning Promise<{value,label}[]>. Loads once on
// mount; surfaces loading + error states inline so the modal stays usable.
function SelectField({ field, issue, value, onChange, disabled }) {
  const [opts, setOpts]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (typeof field.options !== 'undefined') {
      const raw = typeof field.options === 'function' ? field.options(issue) : field.options;
      setOpts(Array.isArray(raw) ? raw : []);
      return undefined;
    }
    if (typeof field.loadOptions === 'function') {
      setLoading(true);
      setErr(null);
      Promise.resolve()
        .then(() => field.loadOptions(issue))
        .then((list) => { if (!cancelled) setOpts(Array.isArray(list) ? list : []); })
        .catch((e) => { if (!cancelled) setErr(e.message || String(e)); })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
    setOpts([]);
    return undefined;
    // field is a stable config object; depending on issue.id covers the
    // realistic re-render case (switching to a different issue).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue?.id]);

  return (
    <label className="cg-resolve-field cg-resolve-field--select">
      <span className="cg-resolve-field-label">
        {field.label}
        {field.required && <span className="cg-resolve-required" aria-hidden="true"> *</span>}
      </span>
      {loading ? (
        <span className="cg-resolve-empty">Loading options…</span>
      ) : err ? (
        <span className="cg-resolve-error" role="alert">Couldn't load options: {err}</span>
      ) : (opts && opts.length === 0) ? (
        <span className="cg-resolve-empty">{field.emptyMessage || 'No options available.'}</span>
      ) : (
        <select
          value={value || ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{field.placeholder || '— select —'}</option>
          {(opts || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
    </label>
  );
}
